// Foundations
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// RXJS
import { filter, map, take, finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { InterviewRequestService } from '@app/services/interview-request.service';
import { AuthService } from '@app/auth/auth.service';
import {
  Breed,
  GetBreedsGQL,
  GetPetsWithStatusGQL,
  GetUserLastNameGQL,
  Pet,
  PetBreed,
} from '@app/services/graphql.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { BookingService } from '@app/services/booking.service';

// Models
import { PetStatusType } from '@app/models/pet/pet-status.model';

// Components

import {
  PetTileFormComponent,
  PetTileFormInfo,
} from '@app/components/interview-request/pet-tile-form/pet-tile-form.component';

// Pipes
import { UnitConversionPipe } from '@app/pipes/unit-conversion.pipe';

@Component({
  selector: 'app-step-one',
  templateUrl: './step-one.component.html',
  styleUrls: ['./step-one.component.scss'],
})
export class StepOneComponent implements OnInit {
  @Output() navigateToStepTwo = new EventEmitter();
  @ViewChild('ptForm', { static: false }) ptForm: PetTileFormComponent;

  form: FormGroup;
  petTileList$: Observable<Pet[]>;
  petList: Pet[] = [];
  isErrorOnSave = false;
  breedList: Breed[];
  userLastName: string;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private _analytics: AnalyticsService,
    private authService: AuthService,
    private bookingService: BookingService,
    private getPetsWithStatusQuery: GetPetsWithStatusGQL,
    private getBreedsQuery: GetBreedsGQL,
    private interviewRequestService: InterviewRequestService,
    private loadingIndicatorService: LoadingIndicatorService,
    private getUserLastNameQuery: GetUserLastNameGQL,
    private convertUnit: UnitConversionPipe
  ) {
    this.form = this.fb.group({
      petArr: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.getUserLastNameQuery
          .fetch({ accountId })
          .pipe(map((res) => res.data.getAccount))
          .subscribe(
            (account) => {
              this.userLastName = account.lastName;
              this.fetchData();
              this.getBreedsQuery.fetch().subscribe((result) => {
                this.breedList = result.data.getBreeds;
              });
            },
            // handle error instead of finalize cause we only want to hide if there is an error
            (error) => {
              this.loadingIndicatorService.hide();
            }
          );
      });
  }

  // pet tile forms

  get petFormArray(): FormArray {
    return this.form.get('petArr') as FormArray;
  }

  createPet(petId, name, isPetSelected, lastName) {
    return this.fb.group({
      petId: [petId],
      name: [name],
      firstName: '',
      lastName,
      breedArr: this.fb.array([]),
      weight: [''],
      isPetSelected: [isPetSelected],
      agreementArr: this.fb.array([]),
      petDetails: [''],
    });
  }

  addPet(petId, petName, isPetSelected = false) {
    this._analytics.logLinkTap('requestInterview_addNewDog');
    this.petFormArray.push(
      this.createPet(petId, petName, isPetSelected, this.userLastName)
    );
  }

  formatInput() {
    const formattedInput: PetTileFormInfo[] = [];
    this.petFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.controls.isPetSelected.value) {
        const breedList: PetBreed[] = [];
        (fg.get('breedArr') as FormArray).controls.forEach(
          (brFg: FormGroup) => {
            breedList.push({
              breed: brFg.controls.name.value,
              isPrimary: false,
            });
          }
        );

        formattedInput.push({
          id: fg.controls.petId.value,
          name: fg.controls.name.value,
          firstName: fg.controls.firstName.value,
          lastName: fg.controls.lastName.value,
          breeds: breedList,
          weight: +fg.controls.weight.value,
          details: fg.controls.petDetails.value,
        });
      }
    });

    return formattedInput;
  }

  onSave() {
    this._analytics.logButtonTap('interviewRequest_saveStepForDogs');
    // check if selected pets have all agreements checked
    let areAllAgreementsChecked = true;
    this.petFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.controls.isPetSelected.value) {
        (fg.get('agreementArr') as FormArray).controls.forEach(
          (agFg: FormGroup) => {
            if (!agFg.controls.isSelected.value) {
              areAllAgreementsChecked = false;
            }
          }
        );
      }
    });

    if (!areAllAgreementsChecked) {
      this.isErrorOnSave = true;
    } else {
      this.isErrorOnSave = false;
      this.interviewRequestService.updateInterviewPetList(this.formatInput());
      this.navigateToStepTwo.emit();
    }
  }

  get isSaveEnabled() {
    // check if at least one pet is selected
    let isAtLeastOnePetSelected = false;
    this.petFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.controls.isPetSelected.value) {
        isAtLeastOnePetSelected = true;
      }
    });

    // check if newly-added pets that are selected, have valid input
    let hasValidInput = true;
    this.petFormArray.controls
      .slice(this.petList.length)
      .forEach((fg: FormGroup) => {
        if (
          fg.controls.isPetSelected.value &&
          (fg.controls.firstName.value === '' ||
            fg.controls.lastName.value === '' ||
            ((fg.controls.breedArr as FormArray).at(0) as FormGroup).controls
              .name.value === '' ||
            !fg.controls.weight.value)
        ) {
          hasValidInput = false;
        }
      });

    return isAtLeastOnePetSelected && hasValidInput;
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private fetchData(): void {
    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.petTileList$ = this.getPetsWithStatusQuery
          .fetch({ accountId })
          .pipe(
            map((res) => {
              const petId = this.route.snapshot.queryParams.petId;
              this.loadingIndicatorService.hide();
              return res.data.getPetsWithStatus
                .filter(
                  (pet) =>
                    pet.active &&
                    pet.statusId === PetStatusType.PENDING_INTERVIEW &&
                    !this.bookingService.hasInterviewEvent(
                      pet.upcomingBookings.concat(
                        pet.waitlistBookings,
                        pet.reservationRequests
                      )
                    )
                )
                .map((pet) => {
                  const { id, firstName, lastName, nickName, weight, breeds } =
                    pet;
                  this.addPet(
                    pet.id,
                    pet.nickName ? pet.nickName : pet.firstName,
                    petId === pet.id
                  );
                  this.petList.push({
                    id,
                    firstName,
                    lastName,
                    nickName,
                    weight,
                    breeds,
                  });
                  return { id, firstName, lastName, nickName, weight, breeds };
                });
            }),
            finalize(() => this.loadingIndicatorService.hide())
          );
      });
  }
}
